import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAllApartments } from '../../data/apartments';
import { locationContent } from '../../data/location';
import { trackEvent } from '../../services/analyticsService';
import { submitInquiry } from '../../services/inquiryService';
import { validateInquiry, hasErrors, type ValidationErrors } from '../../utils/validation';
import type { CreateInquiryRequest } from '../../types';

const initial: CreateInquiryRequest = {
  apartmentId: 0,
  guestName: '',
  guestEmail: '',
  guestPhone: '',
  checkInDate: '',
  checkOutDate: '',
  numAdults: 2,
  numChildren: 0,
  message: '',
  honeypot: '',
};

const Icon = ({ d }: { d: string }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);

export default function InquirySection() {
  const { t } = useTranslation();
  const apartments = getAllApartments();
  const directContactMessage = t('inquiry.directContactMessage', {
    email: locationContent.email,
    phone: locationContent.phone,
  });
  const [form, setForm] = useState<CreateInquiryRequest>(initial);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [banner, setBanner] = useState<string | null>(null);

  useEffect(() => {
    const onPick = (e: Event) => {
      const detail = (e as CustomEvent).detail as { id: number };
      if (detail?.id) setForm((f) => ({ ...f, apartmentId: detail.id }));
    };
    globalThis.addEventListener('villaspilia:selectSuite', onPick);
    return () => globalThis.removeEventListener('villaspilia:selectSuite', onPick);
  }, []);

  const update = <K extends keyof CreateInquiryRequest>(k: K, v: CreateInquiryRequest[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: '' }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBanner(null);
    trackEvent('generate_lead', {
      lead_source: 'website_inquiry_form',
      section_id: 'inquiry',
    });

    const v = validateInquiry(form);
    if (hasErrors(v)) {
      setErrors(v);
      trackEvent('form_error', {
        form_id: 'inquiry',
        section_id: 'inquiry',
      });
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const result = await submitInquiry(form);
      if (result.warning) {
        setSuccess(false);
        setBanner(directContactMessage);
        return;
      }
      trackEvent('form_submit', {
        form_id: 'inquiry',
        section_id: 'inquiry',
      });
      setSuccessMessage(result.message || t('inquiry.successMessage'));
      setSuccess(true);
      setBanner(null);
      setForm(initial);
    } catch (err) {
      trackEvent('exception', {
        description: 'inquiry_submit_failed',
        fatal: false,
      });
      console.warn('Inquiry submission failed, showing direct contact fallback.', err);
      setSuccess(false);
      setBanner(directContactMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <section id="inquiry" className="inquiry section section--ink">
      <div className="container inquiry__inner">
        <aside className="inquiry__aside">
          <p className="eyebrow eyebrow--light reveal">{t('inquiry.eyebrow')}</p>
          <h2 className="reveal">{t('inquiry.title')}</h2>
          <p className="lede reveal">{t('inquiry.subtitle')}</p>

          <ul className="inquiry__assurances">
            <li className="inquiry__assurance reveal">
              <Icon d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
              <div>
                <strong>{t('inquiry.assurances.privacyTitle')}</strong>
                <p>{t('inquiry.assurances.privacyBody')}</p>
              </div>
            </li>
          </ul>
        </aside>

        <form className="inquiry__form reveal" onSubmit={onSubmit} noValidate>
          {success && (
            <output className="form-banner form-banner--success">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12l5 5L20 7" />
              </svg>
              <span>{successMessage || t('inquiry.successMessage')}</span>
              <button type="button" className="form-banner__close" onClick={() => setSuccess(false)} aria-label={t('common.close')}>×</button>
            </output>
          )}
          {banner && (
            <div className="form-banner form-banner--error" role="alert">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{banner}</span>
              <button type="button" className="form-banner__close" onClick={() => setBanner(null)} aria-label={t('common.close')}>×</button>
            </div>
          )}

          <div className="field-grid">
            <label className={`field field--full${errors.guestName ? ' field--error' : ''}`}>
              <span>{t('inquiry.name')}</span>
              <input
                type="text"
                value={form.guestName}
                onChange={(e) => update('guestName', e.target.value)}
                placeholder={t('inquiry.namePlaceholder')}
                required
              />
              {errors.guestName && <span className="field__error">{t(errors.guestName)}</span>}
            </label>

            <label className={`field${errors.guestEmail ? ' field--error' : ''}`}>
              <span>{t('inquiry.email')}</span>
              <input
                type="email"
                value={form.guestEmail}
                onChange={(e) => update('guestEmail', e.target.value)}
                placeholder={t('inquiry.emailPlaceholder')}
                required
              />
              {errors.guestEmail && <span className="field__error">{t(errors.guestEmail)}</span>}
            </label>

            <label className={`field${errors.guestPhone ? ' field--error' : ''}`}>
              <span>{t('inquiry.phone')}</span>
              <input
                type="tel"
                value={form.guestPhone}
                onChange={(e) => update('guestPhone', e.target.value)}
                placeholder={t('inquiry.phonePlaceholder')}
              />
              {errors.guestPhone && <span className="field__error">{t(errors.guestPhone)}</span>}
            </label>

            <label className={`field field--full${errors.apartmentId ? ' field--error' : ''}`}>
              <span>{t('inquiry.apartment')}</span>
              <select
                value={form.apartmentId || ''}
                onChange={(e) => update('apartmentId', Number(e.target.value))}
                required
              >
                <option value="">{t('inquiry.apartmentPlaceholder')}</option>
                {apartments.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              {errors.apartmentId && <span className="field__error">{t(errors.apartmentId)}</span>}
            </label>

            <label className={`field${errors.checkInDate ? ' field--error' : ''}`}>
              <span>{t('inquiry.checkIn')}</span>
              <input
                type="date"
                value={form.checkInDate}
                min={today}
                onChange={(e) => update('checkInDate', e.target.value)}
                required
              />
              {errors.checkInDate && <span className="field__error">{t(errors.checkInDate)}</span>}
            </label>

            <label className={`field${errors.checkOutDate ? ' field--error' : ''}`}>
              <span>{t('inquiry.checkOut')}</span>
              <input
                type="date"
                value={form.checkOutDate}
                min={form.checkInDate || today}
                onChange={(e) => update('checkOutDate', e.target.value)}
                required
              />
              {errors.checkOutDate && <span className="field__error">{t(errors.checkOutDate)}</span>}
            </label>

            <label className={`field${errors.numAdults ? ' field--error' : ''}`}>
              <span>{t('inquiry.numAdults')}</span>
              <input
                type="number"
                min={1}
                max={10}
                value={form.numAdults}
                onChange={(e) => update('numAdults', Number(e.target.value))}
                required
              />
              {errors.numAdults && <span className="field__error">{t(errors.numAdults)}</span>}
            </label>

            <label className={`field${errors.numChildren ? ' field--error' : ''}`}>
              <span>{t('inquiry.numChildren')}</span>
              <input
                type="number"
                min={0}
                max={10}
                value={form.numChildren}
                onChange={(e) => update('numChildren', Number(e.target.value))}
              />
              {errors.numChildren && <span className="field__error">{t(errors.numChildren)}</span>}
            </label>

            <label className={`field field--full${errors.message ? ' field--error' : ''}`}>
              <span>{t('inquiry.message')}</span>
              <textarea
                rows={4}
                maxLength={500}
                value={form.message}
                onChange={(e) => update('message', e.target.value)}
                placeholder={t('inquiry.messagePlaceholder')}
              />
              {errors.message && <span className="field__error">{t(errors.message)}</span>}
            </label>

            {/* Honeypot — hidden from real users */}
            <div className="honeypot" aria-hidden="true">
              <label>
                <span>Website</span>
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.honeypot}
                  onChange={(e) => update('honeypot', e.target.value)}
                />
              </label>
            </div>
          </div>

          <button type="submit" className="btn btn--gold" disabled={submitting}>
            {submitting ? t('inquiry.submitting') : t('inquiry.submit')}
            <span className="arrow" aria-hidden="true">→</span>
          </button>
        </form>
      </div>
    </section>
  );
}
